import classNames from 'classnames';
import { useCallback, useMemo, type FunctionComponent } from 'react';
import { useContextSelector } from 'use-context-selector';

import { useMemberId } from '@trello/authentication';
import {
  calendarDateTimeFormatter,
  getDateDeltaString,
} from '@trello/dates/i18n';
import { useBoardId } from '@trello/id-context';
import { useIsInboxBoard } from '@trello/personal-workspace';
import { getLocation, getRouteIdFromPathname } from '@trello/router';
import { isCardRoute } from '@trello/router/routes';

import { BUTLER_POWER_UP_ID } from 'app/scripts/data/butler-id';
import { ModelCache } from 'app/scripts/db/ModelCache';
import { pluginRunner } from 'app/scripts/views/internal/plugins/PluginRunner';
import { switchCurrentBoardView } from 'app/src/components/Board/switchCurrentBoardView';
import { BoardMembersContext } from 'app/src/components/BoardMembersContext';
import { getAppCreatorModelForApplication } from 'app/src/components/NotificationsMenu/getAppCreatorModelForApplication';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { ActionAppCreator } from './ActionAppCreator';

import * as styles from './ActionTimestamp.module.less';

interface ActionTimestampProps {
  date: string;
  href: string;
  onClickCallback: () => void;
  isHighlighted?: boolean;
  inline?: boolean;
  appCreator?: {
    id?: string | null;
    name?: string | null;
  } | null;
}

export const ActionTimestamp: FunctionComponent<ActionTimestampProps> = ({
  date: dateValue,
  href,
  onClickCallback,
  isHighlighted,
  inline,
  appCreator,
}) => {
  const memberId = useMemberId();
  const boardId = useBoardId();
  const isMember = useContextSelector(
    BoardMembersContext,
    useCallback((value) => value.isMember(memberId), [memberId]),
  );
  const appCreatorModel = appCreator
    ? {
        ...getAppCreatorModelForApplication(appCreator?.id || ''),
        name: appCreator.name || '',
      }
    : undefined;

  const { pathname } = getLocation();
  const routeId = getRouteIdFromPathname(pathname);
  const isOnCard = isCardRoute(routeId);
  const isInboxCard = useIsInboxBoard();

  const [dateDelta, formattedDate] = useMemo(() => {
    const date = new Date(dateValue);
    return [
      getDateDeltaString(new Date(date)),
      calendarDateTimeFormatter.format(date),
    ];
  }, [dateValue]);

  return (
    <span
      className={classNames(
        styles.timestamp,
        inline && styles['timestamp-inline'],
      )}
    >
      {isInboxCard ? (
        <span>{dateDelta}</span>
      ) : (
        /**
         * When the action is actively highlighted, clicking the timestamp again
         * should remove the hash from the URL.
         */
        <a
          href={isHighlighted || !isOnCard ? '#' : href}
          onClick={onClickCallback}
          title={formattedDate}
        >
          {dateDelta}
        </a>
      )}
      {appCreatorModel?.name && (
        <ActionAppCreator
          appCreator={appCreatorModel}
          canLinkAppCreator
          onClick={(e) => {
            // This logic is similar to onAppCreatorLinkClicked in app/scripts/views/action/ActionView.tsx
            stopPropagationAndPreventDefault(e);
            if (isMember) {
              if (appCreatorModel?.idPlugin === BUTLER_POWER_UP_ID) {
                switchCurrentBoardView({
                  routeParams: {
                    view: 'butler',
                    tab: 'schedule',
                    screen: 'usage',
                  },
                  navigateOptions: {
                    trigger: true,
                  },
                });
              } else {
                // Member is a board member, so can run a plugin
                const board = ModelCache.get('Board', boardId);
                if (board) {
                  pluginRunner.one({
                    plugin: appCreatorModel?.idPlugin,
                    command: 'show-settings',
                    board,
                    el: e.currentTarget,
                  });
                }
              }
            } else {
              // Member is not a board member, take them to the plugin listing
              window.open('/power-ups/' + appCreatorModel?.idPlugin);
            }
          }}
        />
      )}
    </span>
  );
};
